import axios from "axios";

export const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

export const TOKEN_KEY = "kvp_token";

api.interceptors.request.use((c) => {
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) c.headers.Authorization = `Bearer ${tok}`;
  return c;
});

api.interceptors.response.use(
  (r) => r,
  (e) => {
    const url = e.config?.url || "";
    if (e.response?.status === 401 && !url.includes("/auth/")) {
      localStorage.removeItem(TOKEN_KEY);
      if (window.location.pathname !== "/login") window.location.href = "/login";
    }
    return Promise.reject(e);
  }
);

export const errMsg = (e, t) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d) && d.code) return t(`errors.${d.code}`, { d: d.distance, r: d.radius, a: d.accuracy ?? "?" });
  if (typeof d === "string" && d.startsWith("ERR_")) return t(`errors.${d}`);
  if (Array.isArray(d)) return t("errors.ERR_VALIDATION");
  return t("errors.ERR_GENERIC");
};

export const getPosition = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject({ geo: "unsupported" });
    let best = null;
    let id = null;
    const finish = () => {
      clearTimeout(timer);
      if (id !== null) navigator.geolocation.clearWatch(id);
      if (best) resolve(best);
      else reject({ geo: "timeout" });
    };
    const timer = setTimeout(finish, 12000);
    id = navigator.geolocation.watchPosition(
      (p) => {
        const cur = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy) };
        if (!best || cur.accuracy < best.accuracy) best = cur;
        if (best.accuracy <= 25) finish();
      },
      (e) => {
        if (e.code === 1) {
          clearTimeout(timer);
          navigator.geolocation.clearWatch(id);
          reject({ geo: "denied" });
        }
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  });

const TZ = "Europe/Istanbul";
export const LOCALES = { tr: "tr-TR", en: "en-GB", ru: "ru-RU" };

export const fmtTime = (iso) =>
  iso ? new Intl.DateTimeFormat("tr-TR", { hour: "2-digit", minute: "2-digit", timeZone: TZ }).format(new Date(iso)) : "—";

export const fmtDate = (ds, lang, opts = { weekday: "short", day: "numeric", month: "short" }) =>
  new Intl.DateTimeFormat(LOCALES[lang], { ...opts, timeZone: "UTC" }).format(new Date(`${ds}T00:00:00Z`));

export const fmtMin = (m, t) => {
  const h = Math.floor((m || 0) / 60);
  const mm = (m || 0) % 60;
  return h ? `${h} ${t("common.h")} ${mm} ${t("common.m")}` : `${mm} ${t("common.m")}`;
};

export const todayStr = () => new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(new Date());

export const addDays = (ds, n) => {
  const d = new Date(`${ds}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

export const wd = (ds) => (new Date(`${ds}T00:00:00Z`).getUTCDay() + 6) % 7;
export const weekStart = (ds) => addDays(ds, -wd(ds));

export const monthRange = (ds) => {
  const [y, m] = ds.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, "0");
  return [`${y}-${mm}-01`, `${y}-${mm}-${String(last).padStart(2, "0")}`];
};

export const addMonths = (ds, n) => {
  const [y, m] = ds.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 10);
};

export const rangeDays = (s, e) => {
  const out = [];
  for (let d = s; d <= e; d = addDays(d, 1)) out.push(d);
  return out;
};

export const periodRange = (mode, anchor) =>
  mode === "week" ? [weekStart(anchor), addDays(weekStart(anchor), 6)] : monthRange(anchor);

export const shiftPeriod = (mode, anchor, dir) => (mode === "week" ? addDays(anchor, 7 * dir) : addMonths(anchor, dir));

export const downloadCsv = (name, rows) => {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replaceAll('"', '""')}"`).join(";")).join("\n");
  const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};
